/**
 * pluQuestions — client-side glue for the PLU Q&A feature.
 *
 *   • `askPluQuestion` : calls the `askPlu` Cloud Function. Returns the
 *     persisted PluQuestion. Optimistic-update friendly : the subscribe
 *     hook below will pick up the new doc instantly via the snapshot
 *     listener, so callers don't need to manually merge state.
 *
 *   • `subscribePluQuestions` : Firestore onSnapshot on the fiche's
 *     `questions` subcollection, ordered chronologically (oldest first
 *     so the conversation reads top → bottom). Returns the unsubscribe
 *     fn — callers should use the React hook below for ergonomics.
 *
 *   • `usePluQuestions` : React hook wrapping the above. Returns the
 *     loaded array + a loading flag, auto-cleans on unmount.
 *
 * Storage path : /users/{uid}/fiches/{ficheId}/questions/{autoId}.
 * Owner-only read (Firestore rules) ; writes are server-only via the
 * callable so the user can't fake answers or bypass the rate limit.
 */

import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
} from "firebase/firestore";
import { auth, db, functions } from "./firebase";
import type { PluQuestion } from "./types";

const callAskPlu = httpsCallable<
  { ficheId: string; question: string },
  PluQuestion
>(functions, "askPlu", {
  // Claude calls top out around ~10 s for these short prompts ; 60 s is
  // plenty even with a cold start.
  timeout: 60_000,
});

export async function askPluQuestion(args: {
  ficheId: string;
  question: string;
}): Promise<PluQuestion> {
  const res = await callAskPlu(args);
  return res.data;
}

export function subscribePluQuestions(
  ficheId: string,
  cb: (questions: PluQuestion[]) => void,
): Unsubscribe {
  const user = auth.currentUser;
  if (!user) {
    cb([]);
    return () => {};
  }
  const ref = collection(
    db,
    "users",
    user.uid,
    "fiches",
    ficheId,
    "questions",
  );
  // Oldest first — questions read like a conversation. The persisted
  // `createdAt` is an ISO string set server-side at write time.
  const q = query(ref, orderBy("createdAt", "asc"));
  return onSnapshot(q, (snap) => {
    const questions = snap.docs.map((d) => d.data() as PluQuestion);
    cb(questions);
  });
}

export function usePluQuestions(ficheId: string | null): {
  questions: PluQuestion[];
  loading: boolean;
} {
  const [questions, setQuestions] = useState<PluQuestion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ficheId) {
      setQuestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = subscribePluQuestions(ficheId, (q) => {
      setQuestions(q);
      setLoading(false);
    });
    return unsub;
  }, [ficheId]);

  return { questions, loading };
}
